import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, money, BG_META, jobTime } from "@/components/status";
import { Button } from "@/components/ui/button";
import { Wallet, Briefcase, Store, ShieldCheck, CreditCard, ArrowRight, AlertTriangle } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-border rounded-md bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4 text-ocean" />
      </div>
      <div className="font-heading font-black text-3xl text-sapphire mt-2">{value}</div>
    </div>
  );
}

export default function CleanerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [earnings, setEarnings] = useState({});
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/earnings").then((r) => setEarnings(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  const notReady = !user.stripe_onboarded || user.background_check_status !== "approved";
  const upcoming = jobs.filter((j) => ["accepted", "in_progress"].includes(j.status)).slice(0, 5);

  return (
    <div className="space-y-8" data-testid="cleaner-dashboard">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Welcome, {user.name.split(" ")[0]}</h1>
        <p className="text-muted-foreground text-sm mt-1">Your cleaning business at a glance.</p>
      </div>

      {notReady && (
        <div className="border border-signal/40 bg-signal/5 rounded-md p-4 flex items-start gap-3" data-testid="onboarding-banner">
          <AlertTriangle className="w-5 h-5 text-signal mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sapphire">Finish setup to start accepting jobs</div>
            <div className="text-sm text-muted-foreground">
              {!user.stripe_onboarded && "Complete Stripe onboarding. "}
              {user.background_check_status !== "approved" && "Submit and pass your background check."}
            </div>
          </div>
          <Button size="sm" className="bg-signal hover:bg-signal/90" onClick={() => navigate("/cleaner/onboarding")} data-testid="finish-setup">
            Finish Setup
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Wallet} label="Total Earned" value={money(earnings.total_earned)} testid="stat-earned" />
        <Stat icon={Briefcase} label="Jobs Done" value={earnings.jobs_completed || 0} testid="stat-jobs-done" />
        <Stat icon={Store} label="Upcoming" value={earnings.upcoming_jobs || 0} testid="stat-upcoming" />
        <Stat icon={CreditCard} label="Payouts" value={earnings.payouts_count || 0} testid="stat-payouts" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-xl text-sapphire">Upcoming Jobs</h2>
            <button className="text-sm text-ocean flex items-center gap-1" onClick={() => navigate("/cleaner/jobs")} data-testid="view-my-jobs">
              All jobs <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="border border-border rounded-md bg-white divide-y divide-border">
            {upcoming.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No upcoming jobs.</div>}
            {upcoming.map((j) => (
              <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 text-left transition-colors" data-testid={`upcoming-${j.id}`}>
                <div>
                  <div className="font-semibold text-sapphire">{j.property?.name}</div>
                  <div className="text-sm text-muted-foreground">{j.date} · {jobTime(j)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-heading font-bold text-sapphire">{money(j.payout_amount)}</span>
                  <StatusBadge status={j.status} />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading font-bold text-xl text-sapphire mb-3">Verification Status</h2>
          <div className="border border-border rounded-md bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm"><ShieldCheck className="w-4 h-4 text-ocean" /> Background Check</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${BG_META[user.background_check_status]?.cls}`}>{BG_META[user.background_check_status]?.label}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm"><CreditCard className="w-4 h-4 text-ocean" /> Stripe Payouts</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${user.stripe_onboarded ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                {user.stripe_onboarded ? "Connected" : "Not connected"}
              </span>
            </div>
            <Button variant="outline" className="w-full" onClick={() => navigate("/cleaner/marketplace")} data-testid="browse-marketplace">
              <Store className="w-4 h-4 mr-1" /> Browse Marketplace
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
